/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ResourceAdminState } from '@app/app/page-modules/resource/store/admin/resource-admin.state';
import { Select } from '@ngxs/store';
import { Observable, takeUntil } from 'rxjs';
import { CheckboxFormContent, Form } from '../../../../models';

@Component({
  selector: 'ptl-form-checkbox',
  templateUrl: './form-checkbox.component.html',
  styleUrls: ['../form.component.scss'],
})
export class FormCheckboxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private subscriptionEnd$ = new EventEmitter<void>();

  /** Receives the input data object */
  @Input() form?: Form;
  @Input() canRemoveItem?: boolean;
  @Input() position?: number | string;

  /** Emits content data on saveForm() */
  @Output() formElementAdded = new EventEmitter<Form>();

  /** Emits removing event of this form with index */
  @Output() formElementRemoved = new EventEmitter<void>();
  @Output() formElementUpdated = new EventEmitter<Form<CheckboxFormContent>>();

  @ViewChild('formTitleInput') private formTitleInput: ElementRef<HTMLInputElement>;

  @Select(ResourceAdminState.contentStateChanged)
  contentStateChanged$: Observable<boolean>;

  checkboxForm: FormGroup;
  expanded = true;
  hasError = false;
  focused = false;
  saveInProgress = false;
  forceExpand = false;

  constructor(private fb: FormBuilder) {
    this.checkboxForm = this.fb.group({
      title: ['', [Validators.required]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.form?.content) {
      this.checkboxForm.patchValue({
        title: (this.form.content as CheckboxFormContent)?.title,
      });
    }
  }

  ngOnInit(): void {
    if (this.form && !!this.form.uid) {
      const content = this.form.content as CheckboxFormContent;
      this.expanded = this.form.newAddedForm ?? false;
      this.checkboxForm.patchValue({
        title: content.title,
      });
    }

    this.contentStateChanged$.pipe(takeUntil(this.subscriptionEnd$)).subscribe(() => {
      this.saveInProgress = false;
    });

    this.forceExpand = this.form?.content?.forceExpand;
  }

  ngAfterViewInit(): void {
    const titleInput = this.formTitleInput?.nativeElement;
    if (this.forceExpand && titleInput) {
      setTimeout(() => {
        const titleLength = titleInput.value.length;
        titleInput.focus();
        titleInput.setSelectionRange(titleLength, titleLength);
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$?.emit();
  }

  saveForm(): void {
    this.hasError = false;
    if (this.checkboxForm.valid) {
      this.expanded = false;
      this.outputData();
      this.emitFormUpdate();
      this.saveInProgress = true;
    } else {
      this.hasError = true;
    }
  }

  expandForm(): void {
    this.expanded = true;
    this.forceExpand = false;
  }

  collapseForm(event: PointerEvent): void {
    event.stopPropagation();
    this.expanded = false;
    this.forceExpand = false;
  }

  outputData(): void {
    if (this.form && this.checkboxForm) {
      const outputData: Form = {
        ...this.form,
        newAddedForm: false,
        content: {
          ...this.checkboxForm.value,
          type: 'CHECKBOX',
        } as CheckboxFormContent,
      };

      this.formElementAdded.emit(outputData);
    }
  }

  removeForm(): void {
    this.formElementRemoved.emit();
  }

  private emitFormUpdate(): void {
    if (!this.form || !this.checkboxForm) {
      return;
    }

    const value = this.checkboxForm.getRawValue();
    const currentContent = (this.form.content as CheckboxFormContent) ?? ({} as CheckboxFormContent);
    const payload: Form<CheckboxFormContent> = {
      ...(this.form as Form<CheckboxFormContent>),
      content: {
        ...currentContent,
        ...value,
        type: 'CHECKBOX',
        forceExpand: this.forceExpand,
        submitted: currentContent?.submitted ?? false,
        formIsActive: currentContent?.formIsActive ?? false,
        isTemplate: currentContent?.isTemplate ?? false,
      },
    };

    this.form = payload as Form;
    this.formElementUpdated.emit(payload);
  }
}
